// Layout-Erkennung fuer gespeicherte Projekte („Skizze bearbeiten").
//
// Ein Projekt traegt in projects.symmetry die Werte des Presets, mit dem es
// gezeichnet wurde. Bis 26.08.2026 war symmetry ueber alle Presets
// eindeutig. Seit Davids Preset-Neuordnung teilen sich zwei Gruppen
// identische Arrays:
//   {1·2·1, 0·2·2·0, 2·2}      = [1,-2,2,-1] ×2
//   {2·2·2, 0·2·2·2·0}         = [1,-2,3,-3,2,-1] ×2
// Der bisherige "erste Treffer in Dateireihenfolge" restaurierte ein mit
// 0·2·2·0 gezeichnetes Projekt als „6 Masten · 1·2·1": falsches Label,
// halbiertes Mastrechteck (t 0,25/0,75 auf 0/1 abgebildet) und — weil die
// Katalogwinkel des falschen Presets neu angewendet werden — falsche
// Pfeile (Prod-248 am 26.08. beobachtet, Sandbox-Tests 21.09.).
//
// Loesung: bei mehreren Kandidaten entscheidet die GEOMETRIE. Fuer jeden
// Kandidaten wird aus den gespeicherten Mastpunkten das Mastrechteck
// zurueckgerechnet (mastRechteckAus), die Masten daraus neu vorhergesagt
// und der Restfehler zu den echten Punkten gemessen. Das Preset mit dem
// kleinsten Fehler gewinnt — beim richtigen Preset ist er ~0 m, beim
// falschen liegen die Innenmasten um Meter daneben.
import proj4 from "proj4";
import { mastLayouts } from "../../config/mastlayouts";
import { getUtmProj } from "./projection";

// Zwei Zahlenlisten vergleichen (Toleranz, weil angles/symmetry in der
// DB als Float-Arrays liegen).
export const sameNumbers = (a, b) =>
  Array.isArray(a) && Array.isArray(b) && a.length === b.length &&
  a.every((v, i) => Math.abs(Number(v) - Number(b[i])) < 1e-6);

// Mast-Rechteck: die Masten sitzen definitionsgemaess auf den Kanten 0
// und 2 an den Parametern t des Layouts (P = Start + t·(Ende − Start)).
// Aus zwei Masten mit unterschiedlichem t je Kante folgen deren beide
// Eckpunkte. Wurde ein Mast von Hand verschoben, ist das Ergebnis leicht
// schief — als Orientierungshilfe reicht es, gerechnet wird ohnehin mit
// den Mastpunkten selbst. Koordinatensystem beliebig (linear).
export const mastRechteckAus = (layout, mastPoints) => {
  const kante = (nummer) => {
    const idx = layout.edges
      .map((e, i) => (e === nummer ? i : -1))
      .filter((i) => i >= 0 && mastPoints[i]);
    if (idx.length < 2) return null;
    let a = idx[0], b = idx[0];
    idx.forEach((i) => {
      if (layout.t[i] < layout.t[a]) a = i;
      if (layout.t[i] > layout.t[b]) b = i;
    });
    const dt = layout.t[b] - layout.t[a];
    if (Math.abs(dt) < 1e-6) return null;
    const P = mastPoints[a], Q = mastPoints[b];
    const auf = (ziel) => [
      P[0] + ((Q[0] - P[0]) * (ziel - layout.t[a])) / dt,
      P[1] + ((Q[1] - P[1]) * (ziel - layout.t[a])) / dt,
    ];
    return [auf(0), auf(1)];
  };
  const k0 = kante(0);
  const k2 = kante(2);
  if (!k0 || !k2) return null;
  // Kante 0 laeuft rect[0]→rect[1], Kante 2 laeuft rect[2]→rect[3];
  // geschlossen zurueckgeben wie beim Zeichnen.
  return [k0[0], k0[1], k2[0], k2[1], k0[0]];
};

// Mittlerer Abstand (m) zwischen den gespeicherten Masten und den Masten,
// die das Preset auf seinem zurueckgerechneten Rechteck vorhersagt.
// Eingabe in UTM. Infinity, wenn sich kein Rechteck bilden laesst.
export const rasterFehler = (layout, mastPointsUtm) => {
  const rect = mastRechteckAus(layout, mastPointsUtm);
  if (!rect) return Infinity;
  let summe = 0;
  layout.edges.forEach((edge, i) => {
    const start = rect[edge];
    const end = rect[(edge + 1) % 4];
    const x = start[0] + layout.t[i] * (end[0] - start[0]);
    const y = start[1] + layout.t[i] * (end[1] - start[1]);
    summe += Math.hypot(x - mastPointsUtm[i][0], y - mastPointsUtm[i][1]);
  });
  return summe / layout.edges.length;
};

// Mittlere Winkelabweichung (Grad) zwischen den gespeicherten Pfeilen und
// den Blickrichtungen, die das Preset vorgibt: Kantenrichtung um
// (Winkel − 90°) gedreht, exakt wie PointTool.legacyEdgeDirection. Das ist
// die einzige Groesse, die 0·2·2·0 von 2·2 trennt — vier Masten an den
// Ecken eines Rechtecks sind geometrisch auch vier eingerueckte Masten
// eines doppelt so langen Rechtecks; nur die Katalogwinkel (±45 gegen
// 67,5/22,5) unterscheiden sich. Eingabe in UTM, Pfeile als [start, ende].
export const winkelFehler = (layout, mastPointsUtm, arrowsUtm) => {
  const rect = mastRechteckAus(layout, mastPointsUtm);
  if (!rect || !arrowsUtm?.length) return Infinity;
  let summe = 0, n = 0;
  layout.edges.forEach((edge, i) => {
    const arrow = arrowsUtm[i];
    if (!arrow || arrow.length < 2) return;
    const ax = arrow[1][0] - arrow[0][0], ay = arrow[1][1] - arrow[0][1];
    const alen = Math.hypot(ax, ay);
    if (alen < 1e-6) return;
    const A = rect[edge], B = rect[(edge + 1) % 4];
    const ex = B[0] - A[0], ey = B[1] - A[1];
    const elen = Math.hypot(ex, ey);
    if (elen < 1e-6) return;
    const phi = ((layout.angles[i] - 90) * Math.PI) / 180;
    const dx = (ex * Math.cos(phi) - ey * Math.sin(phi)) / elen;
    const dy = (ex * Math.sin(phi) + ey * Math.cos(phi)) / elen;
    // Betrag des Winkels zwischen Vorgabe und gespeichertem Pfeil.
    const cross = dx * (ay / alen) - dy * (ax / alen);
    const dot = dx * (ax / alen) + dy * (ay / alen);
    summe += Math.abs((Math.atan2(cross, dot) * 180) / Math.PI);
    n += 1;
  });
  return n ? summe / n : Infinity;
};

// Laenge (m) der Kante 0 des zurueckgerechneten Mastrechtecks — als
// letzter Rueckfall gegen die Feldlaenge gehalten (2·2 umschliesst das
// Feld, 0·2·2·0 ragt ueber die Enden hinaus, aber beide ~Feldlaenge;
// ein Rechteck von halber Feldlaenge passt zu keinem 2·2).
const kante0Laenge = (layout, mastPointsUtm) => {
  const rect = mastRechteckAus(layout, mastPointsUtm);
  return rect ? Math.hypot(rect[1][0] - rect[0][0], rect[1][1] - rect[0][1]) : NaN;
};

// Preset eines gespeicherten Projekts bestimmen.
// 1. Kandidaten = alle Presets mit identischer symmetry (NIE ueber angles:
//    die Winkel wurden gepflegt, Bestandsprojekte tragen alte Werte).
// 2. Ein Kandidat → fertig.
// 3. Mehrere → Rasterfehler (Mastpositionen); alle innerhalb 1 m vom
//    Minimum bleiben im Rennen (1·2·1 faellt hier raus, 0·2·2·0/2·2 nicht).
// 4. Bleiben mehrere → kleinste Winkelabweichung der gespeicherten Pfeile.
// 5. Immer noch mehrere (keine Pfeile) → Kante 0 am naechsten an der
//    Feld-Langseite; sonst erster Treffer in Dateireihenfolge.
// mastPoints/mastArrows/fieldRectangle in WGS84 [lng, lat], wie GET
// /projects/:id/sketch sie liefert.
export const layoutAusSymmetrie = (symmetry, mastPoints = null, mastArrows = null, fieldRectangle = null) => {
  const kandidaten = mastLayouts.filter((l) => sameNumbers(l.symmetry, symmetry));
  if (kandidaten.length === 0) return null;
  if (kandidaten.length === 1 || !mastPoints?.length) return kandidaten[0];

  const wgs84 = "EPSG:4326";
  const utm = getUtmProj(mastPoints[0]);
  const punkteUtm = mastPoints.map((p) => proj4(wgs84, utm, p));

  // 3. Positionen
  const fehler = kandidaten.map((l) => rasterFehler(l, punkteUtm));
  const minFehler = Math.min(...fehler);
  if (!isFinite(minFehler)) return kandidaten[0];
  let rennen = kandidaten.filter((_, i) => fehler[i] <= minFehler + 1.0);
  if (rennen.length === 1) return rennen[0];

  // 4. Pfeile
  if (mastArrows?.length) {
    const pfeileUtm = mastArrows.map((a) =>
      a && a.length >= 2 ? [proj4(wgs84, utm, a[0]), proj4(wgs84, utm, a[1])] : null
    );
    const wf = rennen.map((l) => winkelFehler(l, punkteUtm, pfeileUtm));
    const minWf = Math.min(...wf);
    if (isFinite(minWf)) {
      const engeres = rennen.filter((_, i) => wf[i] <= minWf + 1.0);
      if (engeres.length === 1) return engeres[0];
      rennen = engeres;
    }
  }

  // 5. Feldlaenge
  if (fieldRectangle?.length >= 3) {
    const f = fieldRectangle.slice(0, 3).map((p) => proj4(wgs84, utm, p));
    const feldLang = Math.max(
      Math.hypot(f[1][0] - f[0][0], f[1][1] - f[0][1]),
      Math.hypot(f[2][0] - f[1][0], f[2][1] - f[1][1])
    );
    let bester = rennen[0], besteAbw = Infinity;
    rennen.forEach((l) => {
      const abw = Math.abs(kante0Laenge(l, punkteUtm) - feldLang);
      if (abw < besteAbw) { bester = l; besteAbw = abw; }
    });
    return bester;
  }
  return rennen[0];
};
